import {Fragment, useContext} from "react";
import PropTypes from "prop-types";
import styled, {css} from "styled-components";
import {Draggable} from "react-beautiful-dnd";
import {faEdit, faTrash, faLock, faCheckDouble} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";

// Contexts
import {AuthContext} from "../../contexts/auth.js";

// Utils
import {COMPONENT, FIELD} from "../../utils/builder.js";
import {getMapFromCoords} from "../../utils/helpers.js";
import {DEFAULT_MARKER} from "../../utils/google/maps.js";

// Components
import ElementOptions from "../../components/ElementOptions.js";
import AddElement from "./AddElement.js";

// Style
import {flex, z} from "../../style/components/mixins.js";
import {pad, radius, status} from "../../style/components/variables.js";
import {
  Abbr,
  Button,
  Inline,
  Label,
  Small,
  SubText,
  Text,
  Warning
} from "../../style/components/general.js";

const RenderFacilityBuilder = ({
  builder,
  setElementLocation,
  handleAdd,
  handleBulk,
  handleFormula,
  handleConditional,
  editElement,
  removeElement,
  activeDroppableId = "",
  inFacilityHistory = false,
  modifications,
  children,
  querying = false,
  markerIconMap,
  activeMarker,
  setActiveMarker,
  active,
  locks,
  readOnly = false,
  groupsOnly = false,
  units,
  view,
  setView,
  handleShowModalStateHistory,
  visibleLocationIds
}) => {
  const {atLeast, currentUser, roleCanAccessResource} = useContext(AuthContext);

  const renderOptions = target => {
    if (active && locks && locks[target.name]) {
      const {user} = locks[target.name];
      const {users} = active;
      if (user !== currentUser.publicId) {
        const match = users.filter(curr => curr.publicId === user)[0];
        if (match)
          return (
            <Lock>
              <FontAwesomeIcon icon={faLock} />
              &nbsp;Locked by {match.name}
            </Lock>
          );
      }
    }

    const isTimeFormula = element => {
      if (!element?.formula?.length) return false;
      if (!element.formula[0].value) return false;
      if (builder.byId[element.formula[0].value]?.type === "time") return true;
      return false;
    };

    return (
      <Options className="renderOptions" element={target.element}>
        {roleCanAccessResource("facility", "update") && (
          <>
            {target.element === FIELD &&
              !isTimeFormula(target) &&
              ["number", "range", "generated"].includes(target.type) && (
                <Option
                  data-testid="element.formula"
                  type="button"
                  title="Add/Edit Formula"
                  onClick={() => handleFormula(target)}>
                  f<SubText>(x)</SubText>
                </Option>
              )}
            {target.element === FIELD && (
              <Option
                data-testid="checksheetBuilder.condition"
                type="button"
                title="Add Condition"
                onClick={() => handleConditional(target)}>
                <FontAwesomeIcon icon={faCheckDouble} />
              </Option>
            )}
            {target.type !== "generated" && (
              <Option
                data-testid="element.editButton"
                type="button"
                onClick={() => editElement(target)}>
                <abbr title="Edit">
                  <FontAwesomeIcon icon={faEdit} />
                </abbr>
              </Option>
            )}
            {(atLeast("super") || !target.tag || target.tag.toLowerCase() !== "regulatory") && (
              <Option
                data-testid="element.deleteButton"
                type="button"
                onClick={() => removeElement(target)}>
                <abbr title="Delete">
                  <FontAwesomeIcon icon={faTrash} />
                </abbr>
              </Option>
            )}
          </>
        )}
      </Options>
    );
  };

  const numOptions = target => {
    if (target.element !== FIELD) {
      if (roleCanAccessResource("facility", "create")) return 2;
      // "Set Location" Button is about the size of 2.5 standard option buttons
      return !target?.address && target.parentName === "" ? 2.5 : 0;
    }

    if (atLeast("creator")) {
      if (target.element !== "field") return 2;
      if (target.type === "number" || target.type === "range") return 4;
      if (target.type === "generated") return 3;
      return 4;
    }

    return 1;
  };

  const modificationHasAttr = (id, ...attrs) =>
    attrs.some(
      attr =>
        modifications &&
        modifications[id] &&
        typeof modifications[id] !== "string" &&
        modifications[id].operation !== "removed" &&
        attr in modifications[id] &&
        modifications[id][attr] !== "added"
    );

  const combineAllIds = () => {
    if (!modifications) return builder.allIds;
    return [
      ...Object.keys(modifications).filter(
        key =>
          modifications[key].operation === "removed" &&
          !modifications[key].parentName &&
          !builder.allIds.includes(key)
      ),
      ...builder.allIds
    ];
  };

  const combineChildren = id => {
    const itemChildren = builder.byId[id]?.children || [];
    if (!modifications) return itemChildren;
    return [
      ...Object.keys(modifications).filter(
        key =>
          modifications[key].operation === "removed" &&
          modifications[key].parentName === id &&
          !itemChildren.includes(key)
      ),
      ...itemChildren
    ];
  };

  const renderElements = (ids, level) =>
    ids
      .filter(id => {
        if (!builder.byId[id]) return false;
        if (groupsOnly) {
          const item = builder.byId[id];
          if (item?.address || item?.help) return true;
          return false;
        }
        return true;
      })
      .map((id, index) => {
        let item = builder.byId[id];
        let removedItem = false;
        let modification = modifications ? modifications[id] : undefined;

        if (
          (!item || item.deleted) &&
          modifications &&
          modifications[id]?.operation === "removed"
        ) {
          item = modifications[id];
          removedItem = true;
          modification = "removed";
        }

        let newLevel;
        if (level === "top") newLevel = "group";
        if (level === "group") newLevel = "component";

        const hiddenByFilter =
          Array.isArray(visibleLocationIds) &&
          !visibleLocationIds.some(
            value => String(value) === String(id) || String(value) === String(item?.id)
          );

        return (
          item && (
            <Draggable
              draggableId={id}
              index={index}
              key={id}
              isDragDisabled={
                !atLeast("creator") ||
                !roleCanAccessResource("facility", "update") ||
                querying ||
                readOnly ||
                !!item.stale ||
                !!item.deleted
              }>
              {provided => (
                <Wrapper
                  {...provided.draggableProps}
                  {...provided.dragHandleProps}
                  ref={provided.innerRef}
                  key={id}
                  readOnly={readOnly ? 1 : 0}
                  hidden={!!item.stale || !!item.deleted}
                  inFacilityHistory={inFacilityHistory ? 1 : 0}
                  data-testid="builder.field">
                  <ElementOptions
                    builder={builder}
                    element={item}
                    activeDroppableId={activeDroppableId}
                    handleAdd={handleAdd}
                    renderOptions={readOnly ? () => {} : renderOptions}
                    setElementLocation={setElementLocation}
                    numOptions={numOptions(item)}
                    readOnly={readOnly}
                    inHistory={inFacilityHistory}
                    inFacilityBuilder
                    modification={modification}
                    units={units}
                    handleShowModalStateHistory={handleShowModalStateHistory}>
                    {(modificationHasAttr(id, "globalPrompt") ||
                      (inFacilityHistory && item.globalPrompt)) && (
                      <Label bold>Global Prompt</Label>
                    )}

                    {modificationHasAttr(id, "globalPrompt") && modifications[id]?.globalPrompt && (
                      <Detail isOld>
                        <DetailLabel bold={!inFacilityHistory}>
                          {modifications[id].globalPrompt}
                        </DetailLabel>
                      </Detail>
                    )}

                    {readOnly && item?.globalPrompt && (
                      <Detail
                        isNew={
                          !removedItem &&
                          !!modifications &&
                          !!modifications[id] &&
                          !!modifications[id].globalPrompt
                        }>
                        <DetailLabel bold={!inFacilityHistory}>{item.globalPrompt}</DetailLabel>
                      </Detail>
                    )}

                    {((item?.address && Object.keys(item.address).length > 0) ||
                      item?.help?.filter(helpItem => !helpItem.id && helpItem.key && helpItem.value)
                        ?.length > 0 ||
                      item.internalIds?.length > 0 ||
                      modificationHasAttr(id, "help") ||
                      modificationHasAttr(id, "internalIds") ||
                      modificationHasAttr(id, "address")) && (
                      <>
                        {(modificationHasAttr(id, "help") ||
                          (inFacilityHistory &&
                            item?.help?.filter(
                              helpItem => !helpItem.id && helpItem.key && helpItem.value
                            )?.length > 0)) && <Label bold>Help</Label>}

                        {modificationHasAttr(id, "help") &&
                          modifications[id]?.help
                            ?.filter(helpItem => !helpItem.id && helpItem.key && helpItem.value)
                            ?.map(({key, value}) => (
                              <Detail key={`${id}-help-old-${key}-${value}`} isOld>
                                <DetailLabel>
                                  <Abbr title={key}>{key}</Abbr>
                                </DetailLabel>
                                <Note>
                                  <NoteText bold={!inFacilityHistory}>{value}</NoteText>
                                </Note>
                              </Detail>
                            ))}

                        {readOnly &&
                          item?.help
                            ?.filter(helpItem => !helpItem.id)
                            ?.map(({key, value}) => (
                              <Detail
                                key={`${id}-help-${key}-${value}`}
                                isNew={
                                  !removedItem &&
                                  !!modifications &&
                                  !!modifications[id] &&
                                  !!modifications[id].help
                                }>
                                <DetailLabel>
                                  <Abbr title={key}>{key}</Abbr>
                                </DetailLabel>
                                <Note>
                                  <NoteText bold={!inFacilityHistory}>{value}</NoteText>
                                </Note>
                              </Detail>
                            ))}

                        {(modificationHasAttr(id, "internalIds") ||
                          (inFacilityHistory &&
                            item?.internalIds?.filter(({key, value}) => key && value)?.length >
                              0)) && <Label bold>Internal IDs</Label>}

                        {modificationHasAttr(id, "internalIds") &&
                          modifications[id].internalIds?.map(
                            ({key, value}) =>
                              key &&
                              value && (
                                <Detail key={`${id}-id-old-${key}-${value}`} isOld>
                                  <DetailLabel>
                                    <Abbr title={key}>{key}</Abbr>
                                  </DetailLabel>
                                  <Note>
                                    <NoteText bold={!inFacilityHistory}>{value}</NoteText>
                                  </Note>
                                </Detail>
                              )
                          )}

                        {item?.internalIds?.map(
                          ({key, value}) =>
                            key &&
                            value && (
                              <Detail
                                key={`${id}-id-${key}-${value}`}
                                isNew={
                                  !removedItem &&
                                  !!modifications &&
                                  !!modifications[id] &&
                                  !!modifications[id].internalIds
                                }>
                                <DetailLabel>
                                  <Abbr title={key?.toUpperCase()}>{key?.toUpperCase()}</Abbr>
                                </DetailLabel>
                                <Note>
                                  <NoteText bold={!inFacilityHistory}>{value}</NoteText>
                                </Note>
                              </Detail>
                            )
                        )}

                        {!inFacilityHistory && item?.address && (
                          <View
                            type="button"
                            disabled={hiddenByFilter}
                            title={hiddenByFilter ? "Hidden by the current filter" : undefined}
                            onClick={() => {
                              setActiveMarker(id);
                              if (view && setView) setView("map");
                            }}
                            active={activeMarker === id}
                            plain>
                            View on Map
                            <Icon
                              icon={
                                markerIconMap && markerIconMap[item.markerId]?.icon
                                  ? markerIconMap && markerIconMap[item.markerId]?.icon
                                  : DEFAULT_MARKER.icon
                              }
                              color={`#${
                                markerIconMap && markerIconMap[item.markerId]?.color
                                  ? markerIconMap && markerIconMap[item.markerId]?.color
                                  : DEFAULT_MARKER.color
                              }`}
                            />
                          </View>
                        )}

                        {!inFacilityHistory && readOnly && item?.address && (
                          <AddressLink
                            href={getMapFromCoords(item.address.lat, item.address.lon)}
                            target="_blank"
                            rel="noreferrer">
                            Get Directions
                          </AddressLink>
                        )}

                        {(modificationHasAttr(id, "address") ||
                          (inFacilityHistory && item?.address)) && <Label bold>Address</Label>}

                        {modificationHasAttr(id, "address") && modifications[id]?.address && (
                          <Detail isOld noFlex>
                            {modifications[id].address.primary && (
                              <Label data-testid="edit.location" bold>
                                <span>Primary</span>
                              </Label>
                            )}
                            {modifications[id].address.line1 && (
                              <Text>
                                <span>{modifications[id].address.line1}</span>
                                <br />
                                {modifications[id].address.line2 &&
                                  modifications[id].address.line2 !== "" && (
                                    <>
                                      <span>{modifications[id].address.line2}</span>
                                      <br />
                                    </>
                                  )}
                                <span>
                                  {modifications[id].address.city} {modifications[id].address.state}
                                  {modifications[id].address.zipCode &&
                                    `, ${modifications[id].address.zipCode}`}
                                </span>
                                <br />
                                <Small>
                                  {modifications[id].address.details &&
                                    modifications[id].address.details.map(
                                      detail =>
                                        Object.keys(detail).length > 0 && (
                                          <Fragment key={detail.key}>
                                            {`${detail.key}: ${detail.value}`}
                                            <br />
                                          </Fragment>
                                        )
                                    )}
                                </Small>
                              </Text>
                            )}
                            {!modifications[id].address.line1 && modifications[id].address.lat && (
                              <Text>
                                <span>Lat: {modifications[id].address.lat}</span>
                                <br />
                                <span>Lon: {modifications[id].address.lon}</span>
                              </Text>
                            )}
                          </Detail>
                        )}

                        {inFacilityHistory && item?.address && (
                          <Detail
                            isNew={
                              !removedItem &&
                              !!modifications &&
                              !!modifications[id] &&
                              !!modifications[id].address
                            }
                            noFlex>
                            {item.address.primary && (
                              <Label data-testid="edit.location" bold>
                                <span>Primary</span>
                              </Label>
                            )}
                            {item.address.line1 && (
                              <Text>
                                <span>{item.address.line1}</span>
                                <br />
                                {item.address.line2 && item.address.line2 !== "" && (
                                  <>
                                    <span>{item.address.line2}</span>
                                    <br />
                                  </>
                                )}
                                <span>
                                  {item.address.city} {item.address.state}
                                  {item.address.zipCode && `, ${item.address.zipCode}`}
                                </span>
                                <br />
                                <Small>
                                  {item.address.details &&
                                    item.address.details.map(
                                      detail =>
                                        Object.keys(detail).length > 0 && (
                                          <Fragment key={detail.key}>
                                            {`${detail.key}: ${detail.value}`}
                                            <br />
                                          </Fragment>
                                        )
                                    )}
                                </Small>
                              </Text>
                            )}
                            {!item.address.line1 && item.address.lat && (
                              <Text>
                                <span>Lat: {item.address.lat}</span>
                                <br />
                                <span>Lon: {item.address.lon}</span>
                              </Text>
                            )}
                          </Detail>
                        )}
                      </>
                    )}
                    {newLevel &&
                      item.children &&
                      !groupsOnly &&
                      renderElements(combineChildren(id), newLevel)}
                  </ElementOptions>
                  {!item.stale && !item.deleted && provided.placeholder}
                </Wrapper>
              )}
            </Draggable>
          )
        );
      });

  return (
    builder && (
      <>
        {renderElements(combineAllIds(), "top")}
        {children}
        {atLeast("creator") &&
          roleCanAccessResource("facility", "create") &&
          handleAdd &&
          !querying &&
          !readOnly && (
            <Wrapper data-testid="builder.field">
              <AddElement builder={builder} addElement={handleAdd} />
            </Wrapper>
          )}

        {querying && builder.allIds.length === 0 && <Text>Search returned 0 results</Text>}
        {readOnly && !querying && builder.allIds.length === 0 && (
          <NoElements>
            <Text>No elements</Text>
          </NoElements>
        )}
        {handleBulk && (
          <>
            <BulkEditSticky>
              <BulkButton type="button" onClick={handleBulk}>
                Save
              </BulkButton>
            </BulkEditSticky>
            <WarningWrapper>
              <Warning>Warning!</Warning>
              <Text quiet>You have one or more unsaved changes!</Text>
            </WarningWrapper>
          </>
        )}
      </>
    )
  );
};

RenderFacilityBuilder.propTypes = {
  builder: PropTypes.objectOf(PropTypes.any).isRequired,
  children: PropTypes.node,
  handleFormula: PropTypes.func,
  handleAdd: PropTypes.func,
  handleConditional: PropTypes.func,
  editElement: PropTypes.func,
  removeElement: PropTypes.func,
  setElementLocation: PropTypes.func,
  inFacilityHistory: PropTypes.bool,
  modifications: PropTypes.objectOf(PropTypes.any),
  handleBulk: PropTypes.func,
  activeDroppableId: PropTypes.string,
  querying: PropTypes.bool,
  markerIconMap: PropTypes.objectOf(PropTypes.any),
  activeMarker: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  setActiveMarker: PropTypes.func.isRequired,
  active: PropTypes.objectOf(PropTypes.any),
  locks: PropTypes.objectOf(PropTypes.any),
  readOnly: PropTypes.bool,
  groupsOnly: PropTypes.bool,
  units: PropTypes.arrayOf(PropTypes.any),
  view: PropTypes.string,
  setView: PropTypes.func,
  handleShowModalStateHistory: PropTypes.func,
  visibleLocationIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string]))
};

// Style Overrides
const Wrapper = styled.div`
  position: relative;
  width: 100%;
  height: max-content;
  padding-top: ${({readOnly, inFacilityHistory}) => (readOnly && !inFacilityHistory ? 0 : pad)}px;

  ${({inFacilityHistory}) =>
    inFacilityHistory &&
    css`
      :first-child {
        padding-top: 0;
      }
    `}
`;

const Lock = styled.p`
  padding: ${pad}px;
`;

const Options = styled.div`
  ${flex("row", "nowrap", "center", "center")};
  z-index: ${z("base")};
  visibility: hidden;
  opacity: 0;
  transition: none;
  min-height: 38px;

  :hover {
    visibility: visible;
    opacity: 1;
  }

  ${({element}) =>
    element === COMPONENT &&
    css`
      top: ${pad / 2}px;
    `};
`;

const Option = styled(Button)`
  margin: 0 0 0 ${pad}px;
  font-weight: bold;
  height: 100%;
`;

const Detail = styled.div`
  ${({noFlex}) =>
    !noFlex &&
    css`
      display: flex;
      justify-content: space-between;
    `};

  margin: ${pad}px 0;

  p,
  span {
    color: ${({theme}) => theme.secondary};

    ${({isNew, isOld}) => {
      if (isNew)
        return css`
          color: ${status.success};
        `;

      if (isOld)
        return css`
          color: ${status.error};
          text-decoration: line-through;
        `;

      return "";
    }}
  }
`;

const DetailLabel = styled(Text)`
  width: 25%;
  padding-right: ${pad}px;
  text-overflow: ellipsis;
  overflow: hidden;
  min-width: 80px;

  ${({fullWidth}) =>
    fullWidth &&
    css`
      width: 100%;
    `};
`;

const Note = styled.div`
  display: flex;
  align-items: center;
  width: 75%;
`;

const NoteText = styled.span`
  font-weight: 700;
  color: ${({theme}) => theme.secondary};
  text-overflow: ellipsis;
  overflow: hidden;
  display: block;

  &:before {
    content: attr(title);
  }
`;

const View = styled.button`
  width: fit-content;
  display: flex;
  align-items: center;
  border-radius: ${radius};

  ${({active, theme}) =>
    active &&
    css`
      border-color: ${theme.primary};
    `};

  &:hover:not(:disabled) {
    text-decoration: underline;
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const Icon = styled(FontAwesomeIcon)`
  padding: ${pad}px 0 ${pad}px ${pad / 2}px;
  fill: ${({color, theme}) => color || theme.primary};
`;

const AddressLink = styled.a`
  width: fit-content;
  color: ${({theme}) => theme.secondary};

  &:hover {
    text-decoration: underline;
  }
`;

const NoElements = styled.div`
  ${flex("row", "nowrap", "center", "center")};
  padding: ${pad}px;
`;

const BulkEditSticky = styled.div`
  bottom: ${pad * 2}px;
  margin-top: 0;
  display: flex;
  position: sticky;
  justify-content: start;
  z-index: ${z("above")};
  height: 20px;
`;

const WarningWrapper = styled(Inline)`
  width: 75%;
  align-items: start;
  justify-self: end;
  justify-content: end;
`;

const BulkButton = styled(Button)`
  width: 150px;
  box-shadow: 0 10px 20px rgb(0 0 0 / 50%);
`;

export default RenderFacilityBuilder;
